import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Save, Plus, Trash2, Upload, Image } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { contentService, adminService } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingAbout, setUploadingAbout] = useState(false);
  const heroImageRef = useRef(null);
  const aboutImageRef = useRef(null);
  const { toast } = useToast();
  
  const [content, setContent] = useState({
    // About Us Content - Chinese
    aboutTitle: '專業僱傭服務',
    aboutDescription1: '安芝僱傭中心是一家專業的僱傭中介公司，專門為香港家庭提供優質的外籍家庭傭工聘請服務。',
    aboutDescription2: '我們的服務包括聘請海外女傭、完約現成女傭、本地現成女傭及續約服務。',
    aboutMission: '「致力提供最佳服務體驗，全面照顧客人所需」',
    aboutImage: 'https://images.unsplash.com/photo-1735424080768-8730f9c8a0e9?w=600&h=700&fit=crop',
    
    // About Us Content - English
    aboutTitleEn: '',
    aboutDescription1En: '',
    aboutDescription2En: '',
    aboutMissionEn: '',
    
    // Homepage Content - Chinese
    heroTitle: '選優質女傭',
    heroSubtitle: '堅持專業配對',
    heroDescription: '專業代辦本地、海外菲傭、印傭聘請服務。致力提供最佳服務體驗，全面照顧客人所需。',
    heroImage: 'https://customer-assets.emergentagent.com/job_hk-maidful/artifacts/56qxxu27_maid%20with%20logo.jpg',
    
    // Homepage Content - English
    heroTitleEn: '',
    heroSubtitleEn: '',
    heroDescriptionEn: '',
    
    // Contact Info
    companyName: '安芝僱傭中心有限公司',
    companyNameEn: 'Maidful Employment Agency Limited',
    phone: '+852 6187 1388',
    whatsapp: '85261871388',
    email: 'info@maidful.com.hk',
    address: '香港九龍',
    addressEn: 'Kowloon, Hong Kong',
    
    // License Info
    licenseNumber: '',
    licenseUrl: 'https://www.labour.gov.hk',
    
    // Social Media & Logo
    logoUrl: '',
    navLogoUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    rednoteUrl: '',
    
    // Homepage Features (4 items)
    features: [
      { icon: '👑', title: '優質', description: '優質女傭，專業配對' },
      { icon: '💝', title: '貼心', description: '優質服務，貼心跟進節省您的時間' },
      { icon: '✅', title: '完善', description: '完善售後，讓僱主放心、安心' },
      { icon: '⚡', title: '簡化', description: '簡化流程，用心配對貼心服務' }
    ],
    
    // About Page - 我們的優勢 (4 items)
    advantages: [
      { icon: '👥', title: '專業團隊', description: '經驗豐富的專業團隊，為您提供最優質的配對服務' },
      { icon: '🏆', title: '信譽保證', description: '持牌經營，政府認可，信譽良好，服務有保障' },
      { icon: '⏰', title: '24小時支援', description: '全天候客戶支援，隨時解答您的疑問' },
      { icon: '✓', title: '售後保證', description: '兩年合約保證，免費更換服務，讓您放心' }
    ],
    
    // About Page - 服務特色 (list of items)
    serviceFeatures: [
      '親自到海外面試女傭，確保質素',
      '專業配對服務，因應家庭需要',
      '全程跟進簽證申請，省時省力',
      '提供完善培訓，確保工作能力',
      '24小時客戶支援熱線',
      '兩年合約保證及免費更換',
      '協助辦理保險及醫療檢查',
      '定期跟進服務，確保滿意度'
    ],
    serviceFeaturesEn: [],
    
    // About Page - 我們的承諾 (3 items)
    commitments: [
      { number: '100%', title: '持牌經營', description: '政府認可合法經營' },
      { number: '10+', title: '年經驗', description: '豐富行業經驗' },
      { number: '1000+', title: '成功個案', description: '服務滿意度高' }
    ]
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      if (response.data) {
        // Use default values if arrays are empty
        const defaultFeatures = [
          { icon: '👑', title: '優質', description: '優質女傭，專業配對', titleEn: '', descriptionEn: '' },
          { icon: '💎', title: '貼心', description: '優質服務，貼心跟進', titleEn: '', descriptionEn: '' },
          { icon: '🛡️', title: '完善', description: '完善售後，無後顧之憂', titleEn: '', descriptionEn: '' },
          { icon: '⚡', title: '簡化', description: '簡化程序，節省時間', titleEn: '', descriptionEn: '' }
        ];
        
        const defaultAdvantages = [
          { icon: '👥', title: '專業團隊', description: '提供優質配對服務', titleEn: '', descriptionEn: '' },
          { icon: '🏆', title: '信譽保證', description: '持牌經營，政府認可', titleEn: '', descriptionEn: '' },
          { icon: '💝', title: '貼心服務', description: '全程跟進，無後顧之憂', titleEn: '', descriptionEn: '' },
          { icon: '🌟', title: '經驗豐富', description: '多年行業經驗', titleEn: '', descriptionEn: '' }
        ];
        
        const defaultCommitments = [
          { number: '100%', title: '持牌經營', description: '政府認可合法經營', titleEn: '', descriptionEn: '' },
          { number: '10+', title: '年經驗', description: '豐富行業經驗', titleEn: '', descriptionEn: '' },
          { number: '1000+', title: '成功個案', description: '服務滿意度高', titleEn: '', descriptionEn: '' }
        ];
        
        // Ensure features have English fields, use defaults if empty
        const features = (response.data.features && response.data.features.length > 0) 
          ? response.data.features.map(f => ({
              ...f,
              titleEn: f.titleEn || '',
              descriptionEn: f.descriptionEn || ''
            }))
          : defaultFeatures;
        
        // Ensure advantages have English fields, use defaults if empty
        const advantages = (response.data.advantages && response.data.advantages.length > 0)
          ? response.data.advantages.map(a => ({
              ...a,
              titleEn: a.titleEn || '',
              descriptionEn: a.descriptionEn || ''
            }))
          : defaultAdvantages;
        
        // Ensure commitments have English fields, use defaults if empty
        const commitments = (response.data.commitments && response.data.commitments.length > 0)
          ? response.data.commitments.map(c => ({
              ...c,
              titleEn: c.titleEn || '',
              descriptionEn: c.descriptionEn || ''
            }))
          : defaultCommitments;
        
        // Ensure serviceFeaturesEn matches serviceFeatures length
        const serviceFeatures = response.data.serviceFeatures || [];
        const serviceFeaturesEn = response.data.serviceFeaturesEn || [];
        while (serviceFeaturesEn.length < serviceFeatures.length) {
          serviceFeaturesEn.push('');
        }
        
        setContent(prev => ({
          ...prev,
          ...response.data,
          features,
          advantages,
          commitments,
          serviceFeaturesEn
        }));
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('Saving content:', content);
      await adminService.content.update(content);
      
      toast({
        title: '成功 / Success',
        description: '內容已保存 / Content saved successfully'
      });
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: '錯誤 / Error',
        description: error.response?.data?.detail || '保存失敗，請再試一次 / Save failed, please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, field, setUploading) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: '錯誤',
        description: '只支援 JPG, PNG, GIF, WEBP 圖片格式',
        variant: 'destructive'
      });
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: '錯誤',
        description: '圖片大小不能超過 5MB',
        variant: 'destructive'
      });
      return;
    }
    
    setUploading(true);
    
    try {
      const response = await adminService.upload.uploadImage(file);
      
      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        updateContent(field, imageUrl);
        
        toast({
          title: '成功',
          description: '圖片已上傳'
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: '錯誤',
        description: '上傳失敗，請再試一次',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const updateContent = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...content.features];
    newFeatures[index][field] = value;
    setContent(prev => ({ ...prev, features: newFeatures }));
  };

  const updateAdvantage = (index, field, value) => {
    const newAdvantages = [...content.advantages];
    newAdvantages[index][field] = value;
    setContent(prev => ({ ...prev, advantages: newAdvantages }));
  };

  const updateServiceFeature = (index, value) => {
    const newServiceFeatures = [...content.serviceFeatures];
    newServiceFeatures[index] = value;
    setContent(prev => ({ ...prev, serviceFeatures: newServiceFeatures }));
  };

  const addServiceFeature = () => {
    setContent(prev => ({
      ...prev,
      serviceFeatures: [...prev.serviceFeatures, ''],
      serviceFeaturesEn: [...(prev.serviceFeaturesEn || []), '']
    }));
  };

  const removeServiceFeature = (index) => {
    setContent(prev => ({
      ...prev,
      serviceFeatures: prev.serviceFeatures.filter((_, i) => i !== index),
      serviceFeaturesEn: (prev.serviceFeaturesEn || []).filter((_, i) => i !== index)
    }));
  };

  const updateServiceFeatureEn = (index, value) => {
    const newServiceFeaturesEn = [...(content.serviceFeaturesEn || [])];
    newServiceFeaturesEn[index] = value;
    setContent(prev => ({ ...prev, serviceFeaturesEn: newServiceFeaturesEn }));
  };

  const updateCommitment = (index, field, value) => {
    const newCommitments = [...content.commitments];
    newCommitments[index][field] = value;
    setContent(prev => ({ ...prev, commitments: newCommitments }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>網站內容管理 / Content Management</h1>
        <p className="text-gray-600 mt-2">編輯首頁、關於我們及聯絡資訊（中英文）/ Edit homepage, about us and contact info (Chinese & English)</p>
      </div>

      <div className="space-y-6">
        {/* Homepage Content */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>首頁內容 / Homepage Content</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Chinese Content */}
            <div className="border-b pb-4 mb-4">
              <h3 className="font-semibold text-gray-700 mb-3">中文內容 Chinese Content</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">主標題（中文）*</label>
                  <Input
                    value={content.heroTitle}
                    onChange={(e) => updateContent('heroTitle', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">副標題（中文）*</label>
                  <Input
                    value={content.heroSubtitle}
                    onChange={(e) => updateContent('heroSubtitle', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">描述文字（中文）*</label>
                <Textarea
                  value={content.heroDescription}
                  onChange={(e) => updateContent('heroDescription', e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            {/* English Content */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-3">📝 English Content 英文內容</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-blue-700">Main Title (English)</label>
                  <Input
                    value={content.heroTitleEn || ''}
                    onChange={(e) => updateContent('heroTitleEn', e.target.value)}
                    placeholder="e.g., Quality Domestic Helpers"
                    className="border-blue-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-blue-700">Subtitle (English)</label>
                  <Input
                    value={content.heroSubtitleEn || ''}
                    onChange={(e) => updateContent('heroSubtitleEn', e.target.value)}
                    placeholder="e.g., Professional Matching"
                    className="border-blue-300"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2 text-blue-700">Description (English)</label>
                <Textarea
                  value={content.heroDescriptionEn || ''}
                  onChange={(e) => updateContent('heroDescriptionEn', e.target.value)}
                  placeholder="e.g., Professional agency services for local and overseas Filipino and Indonesian domestic helpers..."
                  rows={3}
                  className="border-blue-300"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">首頁照片 * Hero Image</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={content.heroImage}
                  onChange={(e) => updateContent('heroImage', e.target.value)}
                  placeholder="https://... 或上傳圖片"
                  className="flex-1"
                />
                <input
                  type="file"
                  ref={heroImageRef}
                  onChange={(e) => handleImageUpload(e.target.files[0], 'heroImage', setUploadingHero)}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => heroImageRef.current?.click()}
                  disabled={uploadingHero}
                >
                  {uploadingHero ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  上傳圖片
                </Button>
              </div>
              {content.heroImage && (
                <img src={content.heroImage} alt="Preview" className="mt-2 w-48 h-48 object-cover rounded border" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* About Us Content */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>關於我們內容 / About Us Content</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Chinese Content */}
            <div className="border-b pb-4 mb-4">
              <h3 className="font-semibold text-gray-700 mb-3">中文內容 Chinese Content</h3>
              <div>
                <label className="block text-sm font-medium mb-2">標題（中文）*</label>
                <Input
                  value={content.aboutTitle}
                  onChange={(e) => updateContent('aboutTitle', e.target.value)}
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">第一段內容（中文）*</label>
                <Textarea
                  value={content.aboutDescription1}
                  onChange={(e) => updateContent('aboutDescription1', e.target.value)}
                  rows={4}
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">第二段內容（中文）*</label>
                <Textarea
                  value={content.aboutDescription2}
                  onChange={(e) => updateContent('aboutDescription2', e.target.value)}
                  rows={4}
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">服務宗旨（中文）*</label>
                <Input
                  value={content.aboutMission}
                  onChange={(e) => updateContent('aboutMission', e.target.value)}
                />
              </div>
            </div>

            {/* English Content */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-3">📝 English Content 英文內容</h3>
              <div>
                <label className="block text-sm font-medium mb-2 text-blue-700">Title (English)</label>
                <Input
                  value={content.aboutTitleEn || ''}
                  onChange={(e) => updateContent('aboutTitleEn', e.target.value)}
                  placeholder="e.g., Professional Employment Services"
                  className="border-blue-300"
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2 text-blue-700">First Paragraph (English)</label>
                <Textarea
                  value={content.aboutDescription1En || ''}
                  onChange={(e) => updateContent('aboutDescription1En', e.target.value)}
                  placeholder="e.g., Maidful Employment Agency is a professional employment agency..."
                  rows={4}
                  className="border-blue-300"
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2 text-blue-700">Second Paragraph (English)</label>
                <Textarea
                  value={content.aboutDescription2En || ''}
                  onChange={(e) => updateContent('aboutDescription2En', e.target.value)}
                  placeholder="e.g., Our services include hiring overseas helpers..."
                  rows={4}
                  className="border-blue-300"
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2 text-blue-700">Mission Statement (English)</label>
                <Input
                  value={content.aboutMissionEn || ''}
                  onChange={(e) => updateContent('aboutMissionEn', e.target.value)}
                  placeholder="e.g., Committed to providing the best service experience..."
                  className="border-blue-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">關於我們照片 * About Us Image</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={content.aboutImage}
                  onChange={(e) => updateContent('aboutImage', e.target.value)}
                  placeholder="https://... 或上傳圖片"
                  className="flex-1"
                />
                <input
                  type="file"
                  ref={aboutImageRef}
                  onChange={(e) => handleImageUpload(e.target.files[0], 'aboutImage', setUploadingAbout)}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => aboutImageRef.current?.click()}
                  disabled={uploadingAbout}
                >
                  {uploadingAbout ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  上傳圖片
                </Button>
              </div>
              {content.aboutImage && (
                <img src={content.aboutImage} alt="About Preview" className="mt-2 w-48 h-48 object-cover rounded border" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* 我們的優勢 - Advantages */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>我們的優勢（4個）/ Our Advantages</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.advantages.map((advantage, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded space-y-4">
                {/* Chinese fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">圖示 (emoji)</label>
                    <Input
                      value={advantage.icon}
                      onChange={(e) => updateAdvantage(index, 'icon', e.target.value)}
                      placeholder="👥"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">標題（中文）</label>
                    <Input
                      value={advantage.title}
                      onChange={(e) => updateAdvantage(index, 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">描述（中文）</label>
                    <Input
                      value={advantage.description}
                      onChange={(e) => updateAdvantage(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Title (English)</label>
                    <Input
                      value={advantage.titleEn || ''}
                      onChange={(e) => updateAdvantage(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Professional Team"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Description (English)</label>
                    <Input
                      value={advantage.descriptionEn || ''}
                      onChange={(e) => updateAdvantage(index, 'descriptionEn', e.target.value)}
                      placeholder="e.g., Experienced professional team..."
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* 服務特色 - Service Features */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>服務特色 / Service Features</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.serviceFeatures.map((feature, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => updateServiceFeature(index, e.target.value)}
                    placeholder="輸入服務特色（中文）..."
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => removeServiceFeature(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <Input
                    value={content.serviceFeaturesEn?.[index] || ''}
                    onChange={(e) => updateServiceFeatureEn(index, e.target.value)}
                    placeholder="Service feature (English)..."
                    className="border-blue-300"
                  />
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={addServiceFeature}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              新增服務特色 / Add Service Feature
            </Button>
          </CardContent>
        </Card>

        {/* 我們的承諾 - Commitments */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>我們的承諾（3個）/ Our Commitments</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.commitments.map((commitment, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded space-y-4">
                {/* Chinese fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">數字</label>
                    <Input
                      value={commitment.number}
                      onChange={(e) => updateCommitment(index, 'number', e.target.value)}
                      placeholder="例如: 100%, 10+, 1000+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">標題（中文）</label>
                    <Input
                      value={commitment.title}
                      onChange={(e) => updateCommitment(index, 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">描述（中文）</label>
                    <Input
                      value={commitment.description}
                      onChange={(e) => updateCommitment(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Title (English)</label>
                    <Input
                      value={commitment.titleEn || ''}
                      onChange={(e) => updateCommitment(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Licensed Operation"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Description (English)</label>
                    <Input
                      value={commitment.descriptionEn || ''}
                      onChange={(e) => updateCommitment(index, 'descriptionEn', e.target.value)}
                      placeholder="e.g., Government approved legal operation"
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>聯絡資訊</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">公司名稱（中文）*</label>
                <Input
                  value={content.companyName}
                  onChange={(e) => updateContent('companyName', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">公司名稱（英文）*</label>
                <Input
                  value={content.companyNameEn}
                  onChange={(e) => updateContent('companyNameEn', e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">電話 *</label>
                <Input
                  value={content.phone}
                  onChange={(e) => updateContent('phone', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">WhatsApp 號碼 *</label>
                <Input
                  value={content.whatsapp}
                  onChange={(e) => updateContent('whatsapp', e.target.value)}
                  placeholder="例如: 85261871388 (不需要+號)"
                />
                <p className="text-xs text-gray-500 mt-1">只需輸入數字，不需要+號或空格</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">電郵 *</label>
                <Input
                  value={content.email}
                  onChange={(e) => updateContent('email', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">地址 *</label>
                <Input
                  value={content.address}
                  onChange={(e) => updateContent('address', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* License Information */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>牌照資訊</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">職業介紹所牌照號碼</label>
                <Input
                  value={content.licenseNumber}
                  onChange={(e) => updateContent('licenseNumber', e.target.value)}
                  placeholder="例如: 77738"
                />
                <p className="text-xs text-gray-500 mt-1">留空則不會在網站顯示</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">勞工處網址</label>
                <Input
                  value={content.licenseUrl}
                  onChange={(e) => updateContent('licenseUrl', e.target.value)}
                  placeholder="https://www.labour.gov.hk"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Media & Logo */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>Logo及社交媒體</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">導航欄 Logo（左上角）</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={content.navLogoUrl}
                    onChange={(e) => updateContent('navLogoUrl', e.target.value)}
                    placeholder="https://... 或上傳圖片"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    id="navLogoUpload"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleImageUpload(file, 'navLogoUrl', () => {});
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('navLogoUpload').click()}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    上傳
                  </Button>
                </div>
                {content.navLogoUrl && (
                  <img src={content.navLogoUrl} alt="Nav Logo Preview" className="mt-2 h-12 object-contain rounded border bg-white p-1" />
                )}
                <p className="text-xs text-gray-500 mt-1">網站左上角的Logo，留空將使用預設</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Footer Logo（頁尾）</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={content.logoUrl}
                    onChange={(e) => updateContent('logoUrl', e.target.value)}
                    placeholder="https://... 或上傳圖片"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    id="footerLogoUpload"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleImageUpload(file, 'logoUrl', () => {});
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('footerLogoUpload').click()}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    上傳
                  </Button>
                </div>
                {content.logoUrl && (
                  <img src={content.logoUrl} alt="Footer Logo Preview" className="mt-2 h-16 object-contain rounded border bg-gray-900 p-2" />
                )}
                <p className="text-xs text-gray-500 mt-1">頁尾的Logo，建議使用透明背景PNG格式</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Facebook 連結</label>
                <Input
                  value={content.facebookUrl}
                  onChange={(e) => updateContent('facebookUrl', e.target.value)}
                  placeholder="https://www.facebook.com/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Instagram 連結</label>
                <Input
                  value={content.instagramUrl}
                  onChange={(e) => updateContent('instagramUrl', e.target.value)}
                  placeholder="https://www.instagram.com/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">小紅書 連結</label>
                <Input
                  value={content.rednoteUrl}
                  onChange={(e) => updateContent('rednoteUrl', e.target.value)}
                  placeholder="https://www.xiaohongshu.com/..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Homepage Features */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>首頁特色（4個）/ Homepage Features</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.features.map((feature, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded space-y-4">
                {/* Chinese fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">圖示</label>
                    <Input
                      value={feature.icon}
                      onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                      placeholder="emoji或圖示"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">標題（中文）</label>
                    <Input
                      value={feature.title}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">描述（中文）</label>
                    <Input
                      value={feature.description}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Title (English)</label>
                    <Input
                      value={feature.titleEn || ''}
                      onChange={(e) => updateFeature(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Quality"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Description (English)</label>
                    <Input
                      value={feature.descriptionEn || ''}
                      onChange={(e) => updateFeature(index, 'descriptionEn', e.target.value)}
                      placeholder="e.g., Quality helpers, professional matching"
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pb-24">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            style={{ backgroundColor: '#00b6a5' }}
            className="text-white"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Save className="h-5 w-5 mr-2" />
            )}
            保存所有更改
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminContent;
